import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  DialogFooter,
  Button,
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from 'dui'
import { Loader2 } from 'lucide-react'

import { getDialogTitle } from '@/lib/utils'
import { Question, useGetCategoriesQuery } from '@/store/question-api'

export type DialogMode = 'add' | 'edit' | 'view'

export interface QuestionDialogProps {
  open: boolean
  onClose: () => void
  data: Question
  mode: DialogMode
  onSave: (updated: Question) => void
  readOnly: boolean
}

export const QuestionDialog = ({
  open,
  onClose,
  data,
  mode,
  onSave,
  readOnly,
}: QuestionDialogProps): JSX.Element => {
  const [localData, setLocalData] = useState<Question>(data)
  const [isSaving, setIsSaving] = useState(false)
  const { data: categories = [], isLoading: catsLoading } = useGetCategoriesQuery()

  useEffect(() => {
    setLocalData(data)
  }, [data])

  const handleSaveClick = async (): Promise<void> => {
    if (!isComplete) return
    setIsSaving(true)
    try {
      await onSave(localData)
    } finally {
      setIsSaving(false)
    }
  }
  const selectedCategoryId = localData.category?.id ?? ''
  const isComplete =
    localData.question?.trim().length > 0 && localData.answer?.trim().length > 0 && !!selectedCategoryId

  const canSave = !readOnly && !isSaving && isComplete

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[60vh] min-h-[300px] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{getDialogTitle(mode)}</DialogTitle>
        </DialogHeader>

        <Input
          value={localData.question}
          onChange={(e) => setLocalData({ ...localData, question: e.target.value })}
          readOnly={readOnly}
        />

        <Textarea
          value={localData.answer}
          onChange={(e) => setLocalData({ ...localData, answer: e.target.value })}
          readOnly={readOnly}
          className="min-h-[200px]"
        />

        <div className="space-y-2">
          <label className="text-sm">Kategorija</label>
          <Select
            value={selectedCategoryId}
            onValueChange={(id) => {
              const cat = categories.find((c) => c.id === id)
              setLocalData({
                ...localData,
                category: id ? cat : undefined,
              })
            }}
            disabled={readOnly || catsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="— Izaberi kategoriju —" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!readOnly && (
          <DialogFooter>
            <Button onClick={handleSaveClick} disabled={!canSave} aria-disabled={!canSave}>
              {isSaving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Sačuvaj
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
